import React, { useEffect, useState, useMemo } from "react";
import api, { todayISO } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, PageHeader, Empty } from "@/components/common";
import { toast } from "sonner";
import { CheckCircle2, XCircle } from "lucide-react";
import { TRACKER_SECTIONS, COUNT_OPTIONS } from "@/lib/trackerConfig";
import SimpleTracker from "@/pages/SimpleTracker";

const YN = ({ v, onChange, label, danger }) => (
  <label className="flex items-center justify-between py-2 border-b border-white/5">
    <span className="text-sm text-slate-300">{label}</span>
    <button type="button" onClick={() => onChange(!v)}
      className={`w-12 h-6 rounded-full flex items-center px-0.5 transition-colors ${v ? (danger ? "bg-[#DC2626]" : "bg-emerald-600") : "bg-slate-600"}`}>
      <span className={`w-5 h-5 rounded-full bg-white transition-transform ${v ? "translate-x-6" : ""}`} />
    </button>
  </label>
);

const SECTIONS = TRACKER_SECTIONS;

const blank = (tid, branch) => ({
  date: todayISO(), technician_id: tid || "", branch: branch || "", meds_on_time: true, dosing_errors: 0, sterility_passed: true,
  equipment_check: true, ot_prepared: true, postop_sterilization: true, consent_captured: true, pre_photos: true, post_photos: true,
  videos_captured: false, patient_educated: true, upsell_identified: false, upsell_details: "", upsell_value: 0,
  procedures_assisted: 0, training_attended: false, tasks_assigned: "", task_completion: "N/A", hours_worked: 8, remarks: "", counts: {},
  reviews: 0, testimonials: 0, training_status: "None", training_topic: "", has_issue: false, issue_note: "",
});

export default function DailyTracker() {
  const { technicians, user, filters } = useApp();
  const isTech = user?.app_role === "technician";
  const [date, setDate] = useState(todayISO());
  const [form, setForm] = useState(blank());
  const [tid, setTid] = useState(isTech ? user.employee_id : "");
  const [completion, setCompletion] = useState([]);
  const [saving, setSaving] = useState(false);

  const techs = useMemo(() => technicians.filter((t) => t.status === "Active" && (filters.branch === "all" || t.branch === filters.branch)), [technicians, filters.branch]);
  const selectedTech = technicians.find((t) => t.employee_id === tid);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCount = (k, v) => setForm((f) => ({ ...f, counts: { ...(f.counts || {}), [k]: v } }));

  useEffect(() => { if (isTech) setTid(user.employee_id); }, [isTech, user]);

  const loadCompletion = () => {
    const params = { date };
    if (filters.branch !== "all") params.branch = filters.branch;
    api.get("/trackers/completion", { params }).then(({ data }) => setCompletion(data)).catch(() => {});
  };
  useEffect(() => { if (!isTech) loadCompletion(); /* eslint-disable-next-line */ }, [date, filters.branch]);

  useEffect(() => {
    if (!tid) return;
    api.get("/trackers", { params: { date, technician_id: tid } }).then(({ data }) => {
      if (data.length) setForm(data[0]);
      else setForm({ ...blank(tid, selectedTech?.branch), date });
    });
    /* eslint-disable-next-line */
  }, [tid, date]);

  const submit = async () => {
    if (!tid) { toast.error("Select a technician"); return; }
    setSaving(true);
    try {
      await api.post("/trackers", { ...form, date, technician_id: tid, branch: selectedTech?.branch || form.branch });
      toast.success("Daily tracker saved");
      if (!isTech) loadCompletion();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const inp = "bg-[#0f172a] border border-white/10 rounded-md px-3 py-2 text-sm w-full focus:ring-2 focus:ring-[#C9A24B] focus:outline-none";

  if (isTech) return <SimpleTracker />;

  return (
    <div className="animate-fade-up" data-testid="daily-tracker-page">
      <PageHeader title="Universal Daily Tracker" subtitle="Every technician logs once marked Present — mapped to all 7 official pillars." />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Card className="p-5">
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-xs text-slate-400">Date</label>
                <input data-testid="tracker-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} />
              </div>
              <div>
                <label className="text-xs text-slate-400">Technician</label>
                <select data-testid="tracker-technician" disabled={isTech} value={tid} onChange={(e) => setTid(e.target.value)} className={inp}>
                  <option value="">Select…</option>
                  {techs.map((t) => <option key={t.employee_id} value={t.employee_id}>{t.name} — {t.branch}</option>)}
                </select>
              </div>
            </div>

            {/* Dosing errors — always highlighted red */}
            <div className="flex items-center justify-between bg-[#DC2626]/10 border border-[#DC2626]/40 rounded-md px-4 py-3 mb-4">
              <div>
                <div className="text-sm font-semibold text-[#DC2626]">Dosing Errors (target: 0)</div>
                <div className="text-[11px] text-slate-400">Never softened — safety critical</div>
              </div>
              <input data-testid="tracker-dosing-errors" type="number" min="0" value={form.dosing_errors}
                onChange={(e) => set("dosing_errors", parseInt(e.target.value || 0))}
                className="w-20 bg-[#0f172a] border border-[#DC2626]/50 rounded-md px-3 py-2 text-center text-[#DC2626] font-bold" />
            </div>

            <div className="grid sm:grid-cols-2 gap-x-8">
              {SECTIONS.map((sec) => (
                <div key={sec.title} className="mb-5">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-[11px] uppercase tracking-wider text-[#C9A24B] font-semibold">{sec.title}</h4>
                    <span className="text-[10px] text-slate-500">Done? · Patients (1–10)</span>
                  </div>
                  {sec.fields.map(([k, label]) => (
                    <div key={k} className="flex items-center gap-2 py-2 border-b border-white/5">
                      <span className="text-sm text-slate-300 flex-1">{label}</span>
                      <button type="button" onClick={() => set(k, !form[k])} data-testid={`toggle-${k}`}
                        className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors shrink-0 ${form[k] ? "bg-emerald-600" : "bg-slate-600"}`}>
                        <span className={`w-5 h-5 rounded-full bg-white transition-transform ${form[k] ? "translate-x-5" : ""}`} />
                      </button>
                      <select data-testid={`count-${k}`} value={form.counts?.[k] ?? ""} onChange={(e) => setCount(k, e.target.value === "" ? null : parseInt(e.target.value))}
                        className="w-[68px] bg-[#0f172a] border border-white/10 rounded-md px-2 py-1.5 text-sm text-center text-[#C9A24B] font-semibold focus:ring-2 focus:ring-[#C9A24B] focus:outline-none shrink-0">
                        <option value="">N/A</option>
                        {COUNT_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {form.upsell_identified && (
              <div className="grid sm:grid-cols-2 gap-4 bg-[#C9A24B]/10 border border-[#C9A24B]/30 rounded-md p-4 mb-4">
                <div>
                  <label className="text-xs text-slate-400">Opportunity Details</label>
                  <input data-testid="tracker-upsell-details" value={form.upsell_details} onChange={(e) => set("upsell_details", e.target.value)} className={inp} placeholder="e.g. Premium skincare package" />
                </div>
                <div>
                  <label className="text-xs text-slate-400">Estimated Value (₹)</label>
                  <input data-testid="tracker-upsell-value" type="number" min="0" value={form.upsell_value} onChange={(e) => set("upsell_value", parseFloat(e.target.value || 0))} className={inp} />
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400">Procedures Assisted Today</label>
                <input data-testid="tracker-procedures" type="number" min="0" value={form.procedures_assisted} onChange={(e) => set("procedures_assisted", parseInt(e.target.value || 0))} className={inp} />
                {form.procedures_assisted > 0 && <p className="text-[10px] text-[#C9A24B] mt-1">→ Add {form.procedures_assisted} entries in Surgery Log</p>}
              </div>
              <div>
                <label className="text-xs text-slate-400">Hours Worked</label>
                <input type="number" step="0.5" value={form.hours_worked} onChange={(e) => set("hours_worked", parseFloat(e.target.value || 0))} className={inp} />
              </div>
              <div>
                <label className="text-xs text-slate-400">Training / Skill Session Attended</label>
                <YN v={form.training_attended} onChange={(v) => set("training_attended", v)} label="Attended" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div><label className="text-xs text-slate-400">Tasks Assigned by Head</label><input value={form.tasks_assigned} onChange={(e) => set("tasks_assigned", e.target.value)} className={inp} /></div>
              <div><label className="text-xs text-slate-400">Task Completion Status</label>
                <select value={form.task_completion} onChange={(e) => set("task_completion", e.target.value)} className={inp}>
                  {["N/A", "Completed", "In Progress", "Pending"].map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div className="mt-2"><label className="text-xs text-slate-400">Remarks</label><input value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className={inp} /></div>

            <div className="grid sm:grid-cols-4 gap-4 mt-3 bg-[#C9A24B]/5 border border-[#C9A24B]/20 rounded-md p-3">
              <div><label className="text-xs text-slate-400">Reviews Collected</label><input data-testid="tracker-reviews" type="number" min="0" max="20" value={form.reviews} onChange={(e) => set("reviews", parseInt(e.target.value || 0))} className={inp} /></div>
              <div><label className="text-xs text-slate-400">Testimonial Videos</label><input data-testid="tracker-testimonials" type="number" min="0" max="20" value={form.testimonials} onChange={(e) => set("testimonials", parseInt(e.target.value || 0))} className={inp} /></div>
              <div><label className="text-xs text-slate-400">Training Today</label><select value={form.training_status} onChange={(e) => set("training_status", e.target.value)} className={inp}><option>None</option><option>Completed</option></select></div>
              <div><label className="text-xs text-slate-400">Issue?</label><select value={form.has_issue ? "Yes" : "No"} onChange={(e) => set("has_issue", e.target.value === "Yes")} className={inp}><option>No</option><option>Yes</option></select></div>
            </div>

            <button data-testid="submit-tracker-btn" onClick={submit} disabled={saving}
              className="mt-5 w-full bg-emerald-600 text-white font-semibold py-2.5 rounded-md hover:brightness-110 transition-all disabled:opacity-50">
              {saving ? "Saving…" : "Submit / Update Daily Tracker"}
            </button>
          </Card>
        </div>

        {!isTech && (
          <Card className="p-5 h-fit">
            <h3 className="font-heading text-lg mb-1">Completion Tracking</h3>
            <p className="text-xs text-slate-500 mb-4">{date} — who has submitted today's tracker</p>
            <div className="text-sm mb-3 flex gap-4">
              <span className="text-emerald-400">{completion.filter((c) => c.submitted).length} submitted</span>
              <span className="text-[#DC2626]">{completion.filter((c) => !c.submitted).length} pending</span>
            </div>
            <div className="space-y-1 max-h-[520px] overflow-y-auto">
              {completion.map((c) => (
                <div key={c.technician_id} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5">
                  <span className="text-slate-300">{c.name}<span className="text-[10px] text-slate-500 ml-1">{c.branch}</span></span>
                  {c.submitted ? <CheckCircle2 size={16} className="text-emerald-400" /> : <XCircle size={16} className="text-[#DC2626]" />}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
